import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Container,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Select,
  MenuItem,
  ClickAwayListener,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PetsIcon from "@mui/icons-material/Pets";
import CategoryIcon from "@mui/icons-material/Category";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import Chip from "@mui/material/Chip";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import axios from "axios";
import config from "../../config";

const SearchSection = ({ categories, services }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [searchCategory, setSearchCategory] = useState("all");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const fetchSuggestions = useCallback(
    async (searchTerm) => {
      if (!searchTerm || searchTerm.length < 2) {
        setSuggestions([]);
        return;
      }

      const results = [];
      const term = searchTerm.toLowerCase();

      // Match categories from Redux
      if (searchCategory === "all" || searchCategory === "animals") {
        const matchedCategories = categories
          .filter(
            (cat) => cat.active && cat.name.toLowerCase().includes(term)
          )
          .slice(0, 3)
          .map((cat) => ({
            type: "category",
            label: cat.name,
            id: cat.id,
            icon: <CategoryIcon sx={{ color: "#FFC001" }} />,
          }));
        results.push(...matchedCategories);
      }

      // Match services from Redux
      if (searchCategory === "all" || searchCategory === "services") {
        const matchedServices = (services || [])
          .filter((svc) => svc.name.toLowerCase().includes(term))
          .slice(0, 3)
          .map((svc) => ({
            type: "service",
            label: svc.name,
            id: svc.id,
            icon: <MiscellaneousServicesIcon sx={{ color: "#FFC001" }} />,
          }));
        results.push(...matchedServices);
      }

      // Fetch listing suggestions from API
      if (searchCategory === "all" || searchCategory === "animals") {
        try {
          const response = await axios.get(
            `${config.SERVER_URL}/api/v1/all_listings?page=1&per_page=5&search=${encodeURIComponent(searchTerm)}`,
            { headers: { "Content-Type": "application/json" } }
          );
          if (response.status === 200 && response.data.data) {
            const listingSuggestions = response.data.data.slice(0, 5).map((item) => ({
              type: "listing",
              label: item.name,
              id: item.id,
              subLabel: `$${item.price}`,
              icon: <PetsIcon sx={{ color: "#FFC001" }} />,
            }));
            results.push(...listingSuggestions);
          }
        } catch (err) {
          // Silently fail — suggestions are non-critical
        }
      }

      setSuggestions(results.slice(0, 8));
    },
    [categories, services, searchCategory]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(query);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, fetchSuggestions]);

  const handleSubmit = () => {
    if (!query.trim()) return;
    setShowSuggestions(false);

    if (searchCategory === "services") {
      navigate(`/services/listings?search=${encodeURIComponent(query)}`);
    } else {
      navigate(`/listings?search=${encodeURIComponent(query)}`);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setShowSuggestions(false);
    setQuery("");

    if (suggestion.type === "category") {
      navigate(`/listings?category_id=${suggestion.id}`);
    } else if (suggestion.type === "service") {
      navigate(`/services/${suggestion.id}`);
    } else if (suggestion.type === "listing") {
      navigate(`/listing/${suggestion.id}`);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (focusedIndex >= 0 && suggestions[focusedIndex]) {
        handleSuggestionClick(suggestions[focusedIndex]);
      } else {
        handleSubmit();
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const popularSearches = [
    { label: "Ball Pythons", path: "/listings?search=ball%20python" },
    { label: "Crested Geckos", path: "/listings?search=crested%20gecko" },
    { label: "Tarantulas", path: "/listings?search=tarantula" },
    { label: "Rare Plants", path: "/listings?search=rare%20plants" },
    { label: "Breeders Near Me", path: "/neighbour" },
  ];

  return (
    <Box
      sx={{
        backgroundColor: "#111",
        py: { xs: 3, md: 4 },
        borderBottom: "2px solid #FFC001",
      }}
    >
      <Container maxWidth="md">
        <Typography
          variant="h5"
          align="center"
          sx={{
            color: "#fff",
            fontWeight: 800,
            mb: 0.5,
            fontSize: { xs: "18px", md: "24px" },
          }}
        >
          Discover Exotic Animals, Plants & More
        </Typography>
        <Typography
          align="center"
          sx={{
            color: "#888",
            fontSize: { xs: "13px", md: "15px" },
            mb: 2,
          }}
        >
          Search thousands of listings from trusted breeders nationwide
        </Typography>
        <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
          <Box sx={{ position: "relative" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#fff",
                borderRadius: "12px",
                overflow: "hidden",
                border: "2px solid #FFC001",
                boxShadow: "0 4px 20px rgba(255, 192, 1, 0.15)",
              }}
            >
              {/* Category dropdown - hidden on mobile */}
              <Select
                value={searchCategory}
                onChange={(e) => setSearchCategory(e.target.value)}
                sx={{
                  minWidth: { xs: 0, sm: "140px" },
                  display: { xs: "none", sm: "flex" },
                  backgroundColor: "#f5f5f5",
                  borderRight: "1px solid #ddd",
                  color: "#333",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "& .MuiSelect-select": {
                    py: 1.5,
                    fontSize: "14px",
                    color: "#333",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "#666",
                  },
                }}
                size="small"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="animals">Animals</MenuItem>
                <MenuItem value="plants">Plants</MenuItem>
                <MenuItem value="minerals">Minerals</MenuItem>
                <MenuItem value="services">Services</MenuItem>
              </Select>

              {/* Search input */}
              <TextField
                inputRef={inputRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setShowSuggestions(true);
                  setFocusedIndex(-1);
                }}
                onFocus={() => {
                  if (suggestions.length > 0) setShowSuggestions(true);
                }}
                onKeyDown={handleKeyDown}
                placeholder='Try "Ball Python", "Crested Gecko", or "Rare Plants"'
                variant="standard"
                fullWidth
                InputProps={{
                  disableUnderline: true,
                  startAdornment: (
                    <InputAdornment position="start" sx={{ ml: 1 }}>
                      <SearchIcon sx={{ color: "#999" }} />
                    </InputAdornment>
                  ),
                  sx: {
                    px: 1,
                    py: 1.2,
                    fontSize: { xs: "14px", md: "16px" },
                    color: "#333",
                    "& input::placeholder": {
                      color: "#999",
                      opacity: 1,
                    },
                  },
                }}
              />

              {/* Search button */}
              <IconButton
                onClick={handleSubmit}
                sx={{
                  backgroundColor: "#FFC001",
                  borderRadius: 0,
                  px: { xs: 1.5, md: 2.5 },
                  alignSelf: "stretch",
                  "&:hover": { backgroundColor: "#e6ac00" },
                }}
              >
                <SearchIcon sx={{ color: "#000", fontSize: { xs: 22, md: 26 } }} />
              </IconButton>
            </Box>

            {/* Suggestions dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <Paper
                elevation={4}
                sx={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 1300,
                  mt: 0.5,
                  borderRadius: "8px",
                  maxHeight: "400px",
                  overflow: "auto",
                }}
              >
                <List disablePadding>
                  {suggestions.map((suggestion, index) => (
                    <ListItem
                      key={`${suggestion.type}-${suggestion.id}`}
                      onClick={() => handleSuggestionClick(suggestion)}
                      sx={{
                        cursor: "pointer",
                        backgroundColor:
                          focusedIndex === index ? "#f5f5f5" : "transparent",
                        "&:hover": { backgroundColor: "#f5f5f5" },
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {suggestion.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={suggestion.label}
                        secondary={
                          suggestion.subLabel ? (
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 600,
                                color: "#333",
                                fontSize: "13px",
                              }}
                            >
                              {suggestion.subLabel}
                            </Typography>
                          ) : (
                            <Typography
                              component="span"
                              sx={{ color: "#999", fontSize: "12px" }}
                            >
                              {suggestion.type === "category"
                                ? "Category"
                                : suggestion.type === "service"
                                ? "Service"
                                : "Listing"}
                            </Typography>
                          )
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
        </ClickAwayListener>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 1,
            mt: 2,
          }}
        >
          <LocalFireDepartmentIcon sx={{ color: "#FFC001", fontSize: 18 }} />
          <Typography sx={{ color: "#666", fontSize: "13px", mr: 0.5 }}>
            Popular:
          </Typography>
          {popularSearches.map((item) => (
            <Chip
              key={item.label}
              label={item.label}
              size="small"
              onClick={() => navigate(item.path)}
              sx={{
                backgroundColor: "#1a1a1a",
                color: "#ccc",
                border: "1px solid #333",
                fontSize: "12px",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#FFC001",
                  color: "#000",
                  borderColor: "#FFC001",
                },
              }}
            />
          ))}
        </Box>
      </Container>
    </Box>
  );
};

const mapStateToProps = ({ Categories, Services }) => ({
  categories: Categories.classes,
  services: Services.services,
});

export default connect(mapStateToProps)(SearchSection);
